import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Eyebrow } from '../components/Eyebrow';
import { StatusChip } from '../components/StatusChip';
import { Button } from '../components/Button';
import { SectionNav } from '../components/property/SectionNav';
import type { SectionNavItem, SectionStatus } from '../components/property/SectionNav';
import { SectionAccordion } from '../components/property/SectionAccordion';
import { QuestionRenderer } from '../components/property/QuestionRenderer';
import { PropertyReviewSummary } from '../components/property/PropertyReviewSummary';
import { MagicLinkPrompt } from '../components/property/MagicLinkPrompt';
import { EmailGate } from '../components/EmailGate';
import { usePropertyQuoteForm } from '../hooks/usePropertyQuoteForm';
import { usePropertyDraft } from '../hooks/usePropertyDraft';
import { submitApplication, PropertyApiError } from '../lib/propertyApi';
import type { SectionId } from '@zg/shared/property';
import { useEffect } from 'react';

/**
 * Adaptive property-quote form host.
 *
 * P-5: real form wiring — classifier → schema engine → question rendering
 * via QuestionRenderer, per-section validation, review summary, in-memory
 * submit (logs payload; real API lands in P-8).
 */
export function PropertyForm() {
  const navigate = useNavigate();
  const {
    classifier,
    schema,
    answers,
    draftId,
    visibleQuestionsFor,
    setAnswer,
    errors,
    validateSection,
    sectionProgress,
    submit,
  } = usePropertyQuoteForm();

  const [expandedSectionId, setExpandedSectionId] = useState<string>(
    schema?.sections[0]?.id ?? 'applicant'
  );
  const [activeId, setActiveId] = useState<string>(
    schema?.sections[0]?.id ?? 'applicant'
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [magicPromptOpen, setMagicPromptOpen] = useState(false);
  const [magicPromptShown, setMagicPromptShown] = useState(false);
  const [emailGateOpen, setEmailGateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const draft = usePropertyDraft(classifier, answers);

  // Offer the magic-link prompt once, after the user has answered at least
  // one question. Skipping just closes the modal; answers still autosave.
  useEffect(() => {
    if (magicPromptShown) return;
    if (draft.magicLinkSent) return;
    if (Object.keys(answers).length >= 1) {
      setMagicPromptOpen(true);
      setMagicPromptShown(true);
    }
  }, [answers, magicPromptShown, draft.magicLinkSent]);

  const navItems: SectionNavItem[] = useMemo(() => {
    if (!schema) return [];
    return schema.sections.map((s) => {
      const p = sectionProgress.find((x) => x.sectionId === s.id);
      const status: SectionStatus = !p
        ? 'pending'
        : p.complete
          ? 'complete'
          : p.answered > 0
            ? 'partial'
            : 'pending';
      const hasErrors = schema.questions
        .filter((q) => q.section === s.id)
        .some((q) => Boolean(errors[q.id]));
      return {
        id: s.id,
        title: s.title,
        status: hasErrors ? 'error' : status,
        progress:
          p && p.total > 0
            ? { answered: p.answered, total: p.total }
            : undefined,
      };
    });
  }, [schema, sectionProgress, errors]);

  if (!classifier || !schema) {
    return (
      <>
        <Navbar />
        <main className="container-site py-20">
          <div className="flex flex-col gap-6 max-w-2xl">
            <StatusChip
              label="PROPERTY_INSURANCE · CLASSIFIER_REQUIRED"
              tone="error"
            />
            <h1 className="font-display font-medium tracking-[-0.02em] text-[clamp(32px,5vw,52px)] leading-[1.05]">
              Start from the classifier.
            </h1>
            <p className="text-ink-muted max-w-prose">
              The adaptive form depends on your classifier answers. Head back
              to the start and take the 90-second intake first.
            </p>
            <div>
              <Button
                type="button"
                variant="primary"
                onClick={() => navigate('/property')}
              >
                Back to classifier
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // After the guard above, schema + classifier are guaranteed non-null. Pin
  // locals so the closures below can reference them without re-narrowing.
  const safeSchema = schema;
  const safeClassifier = classifier;

  function jumpTo(id: string) {
    setActiveId(id);
    setExpandedSectionId(id);
    requestAnimationFrame(() => {
      const el = document.getElementById(`section-${id}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function toggleSection(id: string) {
    setExpandedSectionId((cur) => (cur === id ? '' : id));
    setActiveId(id);
  }

  function saveAndContinue(currentId: SectionId) {
    const missing = validateSection(currentId);
    if (missing.length > 0) {
      return;
    }
    const idx = safeSchema.sections.findIndex((s) => s.id === currentId);
    const next = safeSchema.sections[idx + 1];
    if (next) {
      jumpTo(next.id);
    }
  }

  function handleSubmit() {
    setSubmitError(null);
    const payload = submit();
    if (!payload) {
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const section = safeSchema.questions.find(
          (q) => q.id === firstErrorField
        )?.section;
        if (section) jumpTo(section);
      }
      setSubmitError(
        'Some required fields are missing. Jump to the section with errors and complete them.'
      );
      return;
    }
    setEmailGateOpen(true);
  }

  async function handleEmailGateSubmit({
    email,
    consent,
  }: {
    email: string;
    consent: boolean;
  }) {
    if (!consent) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Final autosave before submit so the server has the freshest draft.
      await draft.saveNow({ email });
      const res = await submitApplication({
        draftId: draftId,
        email,
        consent: true,
      });
      setEmailGateOpen(false);
      navigate(`/property/result/${res.submissionId}`, {
        state: {
          submittedAt: res.submittedAt,
          attachments: res.attachments,
          email,
          applicant: answers['applicant.legalName'] ?? '',
        },
      });
    } catch (err) {
      const msg =
        err instanceof PropertyApiError
          ? err.code === 'invalid_body'
            ? 'Some required fields are missing. Close this and complete the form.'
            : err.code === 'draft_not_found'
              ? 'We lost track of your draft. Refresh the page and try again.'
              : err.code === 'email_delivery_failed'
                ? 'We could not deliver your submission email. Please try again in a minute.'
                : `Submission failed (${err.code}).`
          : err instanceof Error
            ? err.message
            : 'Submission failed.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  // Mark safeClassifier as used — PropertyReviewSummary consumes it below.
  void safeClassifier;

  return (
    <>
      <Navbar />
      <main className="container-site py-12">
        <div className="flex flex-col gap-4 mb-10 max-w-3xl">
          <div className="flex flex-wrap items-center gap-3">
            <StatusChip
              label={`PROPERTY_INSURANCE · ${classifier.propertyType.toUpperCase()} · ${classifier.state} · DRAFT_${draftId.slice(-6)}`}
              tone="active"
            />
            <SaveIndicator
              status={draft.status}
              lastSavedAt={draft.lastSavedAt}
              magicLinkSent={draft.magicLinkSent}
              error={draft.error}
            />
          </div>
          <h1 className="font-display font-medium tracking-[-0.02em] text-[clamp(32px,4.5vw,48px)] leading-[1.05]">
            Your application.
          </h1>
          <p className="text-ink-muted max-w-prose">
            The sections below are tailored to your classifier answers.
            Fill in what you can now; answers autosave to this device while
            you work. A magic-link resume lands in phase P-6.
          </p>
          <Eyebrow>ESTIMATED · 15_MINUTES_FIRST_PASS</Eyebrow>
        </div>

        <div className="flex flex-col lg:flex-row gap-10">
          <SectionNav
            sections={navItems}
            activeId={activeId}
            onJump={jumpTo}
          />

          <div className="flex-1 flex flex-col gap-4 min-w-0">
            {schema.sections.map((s) => {
              const navItem = navItems.find((n) => n.id === s.id);
              const status = navItem?.status ?? 'pending';
              const progress = navItem?.progress;
              const sectionQs = visibleQuestionsFor(s.id);
              const isReview = s.id === 'review';
              return (
                <SectionAccordion
                  key={s.id}
                  id={s.id}
                  title={s.title}
                  eyebrow={s.eyebrow}
                  status={status}
                  progress={progress}
                  expanded={expandedSectionId === s.id}
                  onToggle={() => toggleSection(s.id)}
                  onSaveContinue={
                    isReview ? undefined : () => saveAndContinue(s.id)
                  }
                >
                  {isReview ? (
                    <div className="flex flex-col gap-8">
                      <PropertyReviewSummary
                        schema={schema}
                        classifier={classifier}
                        answers={answers}
                        onEditSection={(id) => jumpTo(id)}
                      />
                      {submitError ? (
                        <p className="font-display text-[0.875rem] text-[#b33a3a]">
                          {submitError}
                        </p>
                      ) : null}
                      <div className="flex flex-wrap gap-3">
                        <Button
                          type="button"
                          variant="primary"
                          onClick={handleSubmit}
                        >
                          Submit application
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() =>
                            jumpTo(schema.sections[0]?.id ?? 'applicant')
                          }
                        >
                          Back to top
                        </Button>
                      </div>
                      <p className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-ink-subtle">
                        &gt; NOT_A_BINDABLE_QUOTE · SUBJECT_TO_UNDERWRITING
                      </p>
                    </div>
                  ) : sectionQs.length === 0 ? (
                    <p className="font-mono text-[0.8125rem] text-ink-subtle">
                      &gt; NO_APPLICABLE_QUESTIONS
                    </p>
                  ) : (
                    <div className="flex flex-col gap-6">
                      {sectionQs.map((q) => (
                        <QuestionRenderer
                          key={q.id}
                          question={q}
                          value={answers[q.id]}
                          onChange={(v) => setAnswer(q.id, v)}
                          error={errors[q.id]}
                        />
                      ))}
                    </div>
                  )}
                </SectionAccordion>
              );
            })}
          </div>
        </div>
      </main>
      <MagicLinkPrompt
        open={magicPromptOpen}
        onClose={() => setMagicPromptOpen(false)}
        onSkip={() => setMagicPromptOpen(false)}
        loading={draft.status === 'saving'}
        onSubmit={async (email) => {
          await draft.saveNow({ email });
          setMagicPromptOpen(false);
        }}
      />
      <EmailGate
        open={emailGateOpen}
        onClose={() => setEmailGateOpen(false)}
        onSubmit={handleEmailGateSubmit}
        submitting={submitting}
      />
      <Footer />
    </>
  );
}

function SaveIndicator({
  status,
  lastSavedAt,
  magicLinkSent,
  error,
}: {
  status: 'idle' | 'saving' | 'saved' | 'error';
  lastSavedAt: string | null;
  magicLinkSent: boolean;
  error: string | null;
}) {
  if (status === 'saving') {
    return (
      <span className="font-mono text-[0.6875rem] text-ink-subtle uppercase tracking-[0.08em]">
        &gt; SAVING&hellip;
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span
        className="font-mono text-[0.6875rem] text-[#b33a3a] uppercase tracking-[0.08em]"
        title={error ?? undefined}
      >
        &gt; SAVE_FAILED
      </span>
    );
  }
  if (status === 'saved' && lastSavedAt) {
    const hhmm = new Date(lastSavedAt).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
    return (
      <span className="font-mono text-[0.6875rem] text-ink-subtle uppercase tracking-[0.08em]">
        &gt; SAVED · {hhmm}
        {magicLinkSent ? ' · RESUME_LINK_EMAILED' : ''}
      </span>
    );
  }
  return null;
}

export default PropertyForm;
